import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Mail, MapPin, Phone } from "lucide-react";
import { BUSINESS, CUSTOMER_TYPES, MATERIAL_OPTIONS, PROJECT_TYPES, SQFT_OPTIONS, TIMELINE_OPTIONS } from "@/lib/content";
import { Eyebrow, Reveal } from "./Reveal";
import { FileDrop } from "./FileDrop";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", phone: "", email: "", address: "", project_type: "", customer_type: "Homeowner", material: "", square_feet: "", timeline: "", message: "" };

const Field = ({ label, id, required, children }) => (
  <label htmlFor={id} className="block">
    <span className="mb-2 block text-[11px] uppercase tracking-[0.18em] text-[#AEB3BC]">{label}{required && <span className="text-[#CDA85B]"> *</span>}</span>
    {children}
  </label>
);

const Select = ({ id, value, onChange, options, placeholder }) => (
  <select id={id} value={value} onChange={onChange} data-testid={`estimate-${id.replace(/_/g, "-")}-select`} className="ks-input appearance-none">
    <option value="">{placeholder}</option>
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

export const EstimateForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const onPrefill = (e) => {
      setDone(false);
      setForm((f) => ({ ...f, ...(e.detail || {}) }));
    };
    window.addEventListener("ks:prefill", onPrefill);
    return () => window.removeEventListener("ks:prefill", onPrefill);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.email.includes("@")) {
      toast.error("Please add your name, phone and a valid email.");
      return;
    }
    setSending(true);
    const data = new FormData();
    Object.entries(form).forEach(([k, v]) => data.append(k, v));
    files.forEach((f) => data.append("files", f));
    try {
      await axios.post(`${API}/estimates`, data);
      toast.success("Estimate request received. We'll be in touch shortly.");
      setDone(true);
      setForm(EMPTY);
      setFiles([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Something went wrong. Please call ${BUSINESS.phone}.`);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="estimate" data-testid="estimate-section" className="relative bg-[#0B0F17] py-24 lg:py-36">
      <div className="mx-auto grid max-w-[1400px] gap-14 px-5 sm:px-8 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <Reveal><Eyebrow>Free Estimate</Eyebrow></Reveal>
          <Reveal delay={0.1}><h2 className="mt-5 font-display text-4xl leading-[1.05] text-[#F5F0E6] sm:text-5xl lg:text-6xl">Let’s price your <span className="italic text-[#CDA85B]">countertops.</span></h2></Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-[#C4C1B9]">Tell us about your kitchen or bathroom project. Photos of your space and a rough drawing with measurements help us send an accurate estimate faster.</p>
            <ul className="mt-10 space-y-5 text-[#ECE6DA]">
              <li><a href={BUSINESS.phoneHref} data-testid="estimate-phone-link" className="flex items-center gap-3 hover:text-[#CDA85B]"><Phone className="h-4 w-4 text-[#CDA85B]" /> {BUSINESS.phone}</a></li>
              <li><a href={`mailto:${BUSINESS.email}`} data-testid="estimate-email-link" className="flex items-center gap-3 hover:text-[#CDA85B]"><Mail className="h-4 w-4 text-[#CDA85B]" /> {BUSINESS.email}</a></li>
              <li><a href={BUSINESS.mapHref} target="_blank" rel="noreferrer" data-testid="estimate-address-link" className="flex items-center gap-3 hover:text-[#CDA85B]"><MapPin className="h-4 w-4 text-[#CDA85B]" /> {BUSINESS.address}</a></li>
            </ul>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="lg:col-span-8">
          {done ? (
            <div data-testid="estimate-success" className="flex min-h-[420px] flex-col items-start justify-center border border-[#CDA85B]/30 bg-[#10151F] p-10">
              <CheckCircle2 className="h-10 w-10 text-[#CDA85B]" strokeWidth={1.3} />
              <h3 className="mt-6 font-display text-4xl text-[#F5F0E6]">Thank you. Your request is in.</h3>
              <p className="mt-3 max-w-lg text-[#C4C1B9]">We will review your details and get back to you as soon as possible. Need it sooner? Call us at {BUSINESS.phone}.</p>
              <button onClick={() => setDone(false)} data-testid="estimate-new-request-btn" className="ks-btn-ghost mt-8">Send another request</button>
            </div>
          ) : (
            <form onSubmit={submit} noValidate data-testid="estimate-form" className="grid gap-6 border border-white/[0.07] bg-[#10151F] p-6 sm:grid-cols-2 sm:p-10">
              <Field label="Full name" id="name" required><input id="name" autoComplete="name" value={form.name} onChange={set("name")} data-testid="estimate-name-input" className="ks-input" /></Field>
              <Field label="Phone" id="phone" required><input id="phone" type="tel" autoComplete="tel" value={form.phone} onChange={set("phone")} data-testid="estimate-phone-input" className="ks-input" /></Field>
              <Field label="Email" id="email" required><input id="email" type="email" autoComplete="email" value={form.email} onChange={set("email")} data-testid="estimate-email-input" className="ks-input" /></Field>
              <Field label="Project address or ZIP" id="address"><input id="address" autoComplete="street-address" value={form.address} onChange={set("address")} data-testid="estimate-address-input" className="ks-input" /></Field>
              <Field label="Project type" id="project_type"><Select id="project_type" value={form.project_type} onChange={set("project_type")} options={PROJECT_TYPES} placeholder="Choose a project" /></Field>
              <Field label="I am a" id="customer_type"><Select id="customer_type" value={form.customer_type} onChange={set("customer_type")} options={CUSTOMER_TYPES} placeholder="Choose one" /></Field>
              <Field label="Material" id="material"><Select id="material" value={form.material} onChange={set("material")} options={MATERIAL_OPTIONS} placeholder="Choose a material" /></Field>
              <Field label="Approx. square feet" id="square_feet"><Select id="square_feet" value={form.square_feet} onChange={set("square_feet")} options={SQFT_OPTIONS} placeholder="Choose a range" /></Field>
              <div className="sm:col-span-2"><Field label="Timeline" id="timeline"><Select id="timeline" value={form.timeline} onChange={set("timeline")} options={TIMELINE_OPTIONS} placeholder="When do you need it?" /></Field></div>
              <div className="sm:col-span-2"><Field label="Project details" id="message"><textarea id="message" rows={4} value={form.message} onChange={set("message")} data-testid="estimate-message-input" placeholder="Cabinet layout, sink type, backsplash, edge profile, removal of old tops…" className="ks-input resize-none" /></Field></div>
              <div className="sm:col-span-2">
                <span className="mb-2 block text-[11px] uppercase tracking-[0.18em] text-[#AEB3BC]">Photos & drawings</span>
                <FileDrop files={files} setFiles={setFiles} />
              </div>
              <div className="flex flex-col gap-4 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-[#AEB3BC]">Free, no-obligation estimate. We never share your information.</p>
                <button type="submit" disabled={sending} data-testid="estimate-submit-btn" className="ks-btn-gold justify-center disabled:opacity-60">
                  {sending ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : "Request My Free Estimate"}
                </button>
              </div>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
